import { useState, useEffect } from "react";
import { Link, useRoute } from "wouter";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { FiMenu, FiX } from "react-icons/fi";
import MobileMenu from "./mobile-menu";

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [isHomeActive] = useRoute("/");
  const [isAboutActive] = useRoute("/about-us");
  const [isProductsActive] = useRoute("/products");
  const [isServicesActive] = useRoute("/services");
  const [isContactActive] = useRoute("/contact");

  const NavLink = ({ to, isActive, children }: { to: string; isActive: boolean; children: React.ReactNode }) => (
    <Link href={to}>
      <motion.span 
        className={`nav-link cursor-pointer ${isActive ? 'active text-primary' : 'text-neutral-900'} font-medium hover:text-primary transition py-4 block`}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.95 }}
      >
        {children}
      </motion.span>
    </Link>
  );

  return (
    <motion.header 
      className={`sticky top-0 z-50 bg-white ${scrolled ? 'shadow-md' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="container mx-auto px-4 py-3 md:py-2">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center justify-between w-full md:w-auto">
            <Link href="/">
              <motion.div 
                className="flex items-center cursor-pointer"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <img 
                  src="/images/varsha-seeds-logo.png" 
                  alt="Varsha Seeds Logo" 
                  className="h-16 w-auto"
                />
              </motion.div>
            </Link>
            <motion.button 
              className="md:hidden text-primary focus:outline-none rounded-full p-2 hover:bg-gray-100"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              whileTap={{ scale: 0.9 }}
            >
              {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </motion.button>
          </div>
          
          <nav className="hidden md:flex w-full md:w-auto mt-4 md:mt-0">
            <ul className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-8 w-full md:w-auto">
              <li>
                <NavLink to="/" isActive={isHomeActive}>Home</NavLink>
              </li>
              <li>
                <NavLink to="/about-us" isActive={isAboutActive}>About Us</NavLink>
              </li>
              <li>
                <NavLink to="/products" isActive={isProductsActive}>Products</NavLink>
              </li>
              <li>
                <NavLink to="/services" isActive={isServicesActive}>Services</NavLink>
              </li>
              <li>
                <NavLink to="/contact" isActive={isContactActive}>Contact</NavLink>
              </li>
              <li className="md:ml-4">
                <Link href="/contact">
                  <Button className="bg-primary hover:bg-primary/90 text-white font-medium rounded-full">
                    Get a Quote
                  </Button>
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </div>
      
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </motion.header>
  );
};

export default Navbar;
